import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { adaptEchartsOptions, BaseEchartsDirective } from '@examdojo/core/widget';
import { EChartsOption } from 'echarts';
import { LineChart, ScatterChart } from 'echarts/charts';
import {
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import type { CorrelationHoverSection } from '../../../../dashboards/correlation.store';
import { fmt, fmtAxisLabel } from '../../../shared/chart-shared';
import {
  DEFAULT_SCATTER_PLOT_DISPLAY,
  type ScatterPlotDisplayConfig,
  type ScatterPlotParams,
  type ScatterPlotPrediction,
} from './scatter-plot-chart.model';
import {
  computeCubicRegression,
  computeExponentialRegression,
  computeInverseRegression,
  computeLogarithmicRegression,
  computeLogisticRegression,
  computePowerRegression,
  computeQuadraticRegression,
  computeRegression,
  computeResiduals,
  computeSineRegression,
  generateCurvePoints,
  predictCubicY,
  predictExponentialY,
  predictLogarithmicY,
  predictLogisticY,
  predictPowerY,
  predictQuadraticY,
  predictSineY,
  predictX,
  predictY,
  minMax,
  type CubicRegressionResult,
  type ExponentialRegressionResult,
  type InverseRegressionResult,
  type LogarithmicRegressionResult,
  type LogisticRegressionResult,
  type PowerRegressionResult,
  type QuadraticRegressionResult,
  type RegressionResult,
  type SineRegressionResult,
} from './scatter-plot-chart.util';

echarts.use([
  ScatterChart,
  LineChart,
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  LegendComponent,
  DataZoomSliderComponent,
  DataZoomInsideComponent,
]);

@Component({
  selector: 'dojo-scatter-plot-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './scatter-plot-chart.component.html',
  styleUrl: './scatter-plot-chart.component.scss',
  host: { class: 'block w-full h-full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScatterPlotChartComponent extends BaseEchartsDirective {
  readonly params = input.required<ScatterPlotParams>();
  readonly display = input<ScatterPlotDisplayConfig>({});
  readonly prediction = input<ScatterPlotPrediction | null>(null);
  readonly hoveredSection = input<CorrelationHoverSection | null>(null);
  readonly hoveredPointIndex = input<number | null>(null);

  readonly pointHover = output<{ index: number | null; source: 'chart' }>();

  readonly regression = computed<RegressionResult | null>(() => {
    const points = this.params().points;
    return computeRegression(points);
  });

  readonly inverseRegression = computed<InverseRegressionResult | null>(() => {
    const points = this.params().points;
    return computeInverseRegression(points);
  });

  readonly quadraticRegression = computed<QuadraticRegressionResult | null>(() => {
    const regressionType = this.display().regressionType;
    if (regressionType !== 'quadratic') {
      return null;
    }
    return computeQuadraticRegression(this.params().points);
  });

  readonly sineRegression = computed<SineRegressionResult | null>(() => {
    const regressionType = this.display().regressionType;
    if (regressionType !== 'sine') {
      return null;
    }
    return computeSineRegression(this.params().points);
  });

  readonly cubicRegression = computed<CubicRegressionResult | null>(() => {
    const regressionType = this.display().regressionType;
    if (regressionType !== 'cubic') {
      return null;
    }
    return computeCubicRegression(this.params().points);
  });

  readonly exponentialRegression = computed<ExponentialRegressionResult | null>(() => {
    const regressionType = this.display().regressionType;
    if (regressionType !== 'exponential') {
      return null;
    }
    return computeExponentialRegression(this.params().points);
  });

  readonly powerRegression = computed<PowerRegressionResult | null>(() => {
    const regressionType = this.display().regressionType;
    if (regressionType !== 'power') {
      return null;
    }
    return computePowerRegression(this.params().points);
  });

  readonly logarithmicRegression = computed<LogarithmicRegressionResult | null>(() => {
    const regressionType = this.display().regressionType;
    if (regressionType !== 'logarithmic') {
      return null;
    }
    return computeLogarithmicRegression(this.params().points);
  });

  readonly logisticRegression = computed<LogisticRegressionResult | null>(() => {
    const regressionType = this.display().regressionType;
    if (regressionType !== 'logistic') {
      return null;
    }
    return computeLogisticRegression(this.params().points);
  });

  readonly chartOptions = computed<EChartsOption>(() => {
    const { points, xLabel, yLabel } = this.params();
    const display = { ...DEFAULT_SCATTER_PLOT_DISPLAY, ...this.display() };
    const regressionType = display.regressionType ?? 'linear';
    const regression = this.regression();
    const inverseRegression = this.inverseRegression();
    const quadraticRegression = this.quadraticRegression();
    const sineRegression = this.sineRegression();
    const cubicRegression = this.cubicRegression();
    const exponentialRegression = this.exponentialRegression();
    const powerRegression = this.powerRegression();
    const logarithmicRegression = this.logarithmicRegression();
    const logisticRegression = this.logisticRegression();
    const pred = this.prediction();
    const hoveredSection = this.hoveredSection();
    const hoveredPointIndex = this.hoveredPointIndex();

    if (points.length === 0) {
      return buildEmptyChart(xLabel, yLabel);
    }

    // Resolve colors
    const dotColor = display.dotColor ?? '#0ea5e9';
    const regressionColor = display.regressionLineColor ?? '#8b5cf6';
    const residualColor = display.residualColor ?? '#ef4444';
    const inverseRegressionColor = display.inverseRegressionLineColor ?? '#ef4444';

    // Only highlight regression line when hovering the equation itself
    const isRegressionHovered = hoveredSection === 'equation';

    // Compute axis ranges (single-pass, no stack overflow)
    const xBounds = minMax(points.map((p) => p.x));
    const yBounds = minMax(points.map((p) => p.y));
    const xMin = xBounds.min;
    const xMax = xBounds.max;
    const yMin = yBounds.min;
    const yMax = yBounds.max;
    const xRange = xMax - xMin || 1;
    const yRange = yMax - yMin || 1;
    const xPadding = xRange * 0.08;
    const yPadding = yRange * 0.08;

    let axisXMin = xMin - xPadding;
    let axisXMax = xMax + xPadding;
    let axisYMin = yMin - yPadding;
    let axisYMax = yMax + yPadding;

    // Extend axis to include prediction point (especially for extrapolation)
    if (pred && regression) {
      axisXMin = Math.min(axisXMin, pred.x - xPadding);
      axisXMax = Math.max(axisXMax, pred.x + xPadding);
      axisYMin = Math.min(axisYMin, pred.y - yPadding);
      axisYMax = Math.max(axisYMax, pred.y + yPadding);
    }

    // Extend axis to include y-intercept when intercept is hovered
    if (hoveredSection === 'intercept' && regression) {
      axisXMin = Math.min(axisXMin, -xPadding);
      axisYMin = Math.min(axisYMin, regression.b - yPadding);
      axisYMax = Math.max(axisYMax, regression.b + yPadding);
    }

    // Extend axis to include y-intercept when explaining regression line
    if (display.explainRegressionLine && display.showRegressionLine && regression) {
      axisXMin = Math.min(axisXMin, -Math.ceil(xPadding));
      axisYMin = Math.min(axisYMin, Math.floor(regression.b));
    }

    // Snap axis bounds to clean numbers so tick labels don't show ugly decimals
    const xTick = niceStep(xRange);
    const yTick = niceStep(yRange);
    axisXMin = Math.floor(axisXMin / xTick) * xTick;
    axisXMax = Math.ceil(axisXMax / xTick) * xTick;
    axisYMin = Math.floor(axisYMin / yTick) * yTick;
    axisYMax = Math.ceil(axisYMax / yTick) * yTick;

    const REGRESSION_LEGEND_NAMES: Record<string, string> = {
      linear: 'Y on X Line',
      quadratic: 'Quadratic Fit',
      cubic: 'Cubic Fit',
      exponential: 'Exponential Fit',
      power: 'Power Fit',
      logarithmic: 'Logarithmic Fit',
      logistic: 'Logistic Fit',
      sine: 'Sine Fit',
    };
    const regressionLegendName = REGRESSION_LEGEND_NAMES[regressionType] ?? 'Regression';

    const nonLinearRegressions: Record<string, unknown> = {
      quadratic: quadraticRegression,
      cubic: cubicRegression,
      exponential: exponentialRegression,
      power: powerRegression,
      logarithmic: logarithmicRegression,
      logistic: logisticRegression,
      sine: sineRegression,
    };
    const hasActiveRegression = (regressionType === 'linear' && !!regression) || !!nonLinearRegressions[regressionType];

    const showLegend = !!(
      display.showRegressionLine ||
      display.showInverseRegressionLine ||
      display.showResiduals ||
      display.showMeanPoint
    );
    const zoomX = !!display.dataZoomX;
    const zoomY = !!display.dataZoomY;

    const series: NonNullable<EChartsOption['series']> = [];

    // Scatter points
    const defaultDotSize = display.dotSize ?? 10;
    const pointLabelColor = display.pointLabelColor ?? dotColor;
    series.push({
      type: 'scatter',
      name: 'Observation',
      data: points.map((p, i) => ({
        value: [p.x, p.y],
        symbolSize: hoveredPointIndex === i ? defaultDotSize + 6 : defaultDotSize,
        itemStyle:
          hoveredPointIndex === i
            ? { color: '#f59e0b', borderColor: '#fff', borderWidth: 2 }
            : hoveredPointIndex !== null
              ? { color: dotColor, opacity: 0.3 }
              : { color: dotColor },
      })),
      symbolSize: defaultDotSize,
      itemStyle: { color: dotColor },
      ...(display.showPointLabels
        ? {
            label: {
              show: true,
              position: 'top' as const,
              fontSize: 11,
              fontWeight: 600,
              color: pointLabelColor,
              formatter: (params: { dataIndex?: number }) => String.fromCharCode(65 + (params.dataIndex ?? 0)),
            },
          }
        : {}),
      z: 3,
    });

    // Regression line / curve (Y on X)
    if (display.showRegressionLine && regressionType === 'linear' && regression) {
      const lineStart = predictY(regression, axisXMin);
      const lineEnd = predictY(regression, axisXMax);

      series.push({
        type: 'line',
        name: 'Y on X Line',
        data: [
          [axisXMin, lineStart],
          [axisXMax, lineEnd],
        ],
        symbol: 'none',
        lineStyle: {
          color: regressionColor,
          width: isRegressionHovered ? 4 : 2,
          shadowBlur: isRegressionHovered ? 8 : 0,
          shadowColor: isRegressionHovered ? regressionColor : 'transparent',
        },
        z: 2,
        silent: true,
      });
    }

    // Quadratic regression curve
    if (display.showRegressionLine && regressionType === 'quadratic' && quadraticRegression) {
      const curveData = generateCurvePoints((x) => predictQuadraticY(quadraticRegression, x), axisXMin, axisXMax);

      series.push({
        type: 'line',
        name: 'Quadratic Fit',
        data: curveData,
        showSymbol: false,
        lineStyle: {
          color: regressionColor,
          width: isRegressionHovered ? 4 : 2.5,
          shadowBlur: isRegressionHovered ? 8 : 0,
          shadowColor: isRegressionHovered ? regressionColor : 'transparent',
        },
        itemStyle: { color: regressionColor },
        z: 2,
        silent: true,
      });
    }

    // Sinusoidal regression curve
    if (display.showRegressionLine && regressionType === 'sine' && sineRegression) {
      const curveData = generateCurvePoints((x) => predictSineY(sineRegression, x), axisXMin, axisXMax);

      series.push({
        type: 'line',
        name: 'Sine Fit',
        data: curveData,
        showSymbol: false,
        lineStyle: {
          color: regressionColor,
          width: isRegressionHovered ? 4 : 2.5,
          shadowBlur: isRegressionHovered ? 8 : 0,
          shadowColor: isRegressionHovered ? regressionColor : 'transparent',
        },
        itemStyle: { color: regressionColor },
        z: 2,
        silent: true,
      });
    }

    // Generic non-linear curve builder for remaining types
    const curveBuilders: Record<string, (() => number) | undefined> = {};
    if (cubicRegression) {
      curveBuilders['cubic'] = undefined;
    }
    if (exponentialRegression) {
      curveBuilders['exponential'] = undefined;
    }
    if (powerRegression) {
      curveBuilders['power'] = undefined;
    }
    if (logarithmicRegression) {
      curveBuilders['logarithmic'] = undefined;
    }
    if (logisticRegression) {
      curveBuilders['logistic'] = undefined;
    }

    const predictFns: Record<string, (x: number) => number> = {
      ...(cubicRegression ? { cubic: (x: number) => predictCubicY(cubicRegression, x) } : {}),
      ...(exponentialRegression ? { exponential: (x: number) => predictExponentialY(exponentialRegression, x) } : {}),
      ...(powerRegression ? { power: (x: number) => predictPowerY(powerRegression, x) } : {}),
      ...(logarithmicRegression ? { logarithmic: (x: number) => predictLogarithmicY(logarithmicRegression, x) } : {}),
      ...(logisticRegression ? { logistic: (x: number) => predictLogisticY(logisticRegression, x) } : {}),
    };

    const predictFn = predictFns[regressionType];
    if (display.showRegressionLine && predictFn) {
      const curveXMin =
        regressionType === 'power' || regressionType === 'logarithmic' ? Math.max(axisXMin, 0.001) : axisXMin;
      const curveData = generateCurvePoints(predictFn, curveXMin, axisXMax);

      series.push({
        type: 'line',
        name: regressionLegendName,
        data: curveData,
        showSymbol: false,
        lineStyle: {
          color: regressionColor,
          width: isRegressionHovered ? 4 : 2.5,
          shadowBlur: isRegressionHovered ? 8 : 0,
          shadowColor: isRegressionHovered ? regressionColor : 'transparent',
        },
        itemStyle: { color: regressionColor },
        z: 2,
        silent: true,
      });
    }

    // Inverse regression line (X on Y)
    if (display.showInverseRegressionLine && inverseRegression) {
      const xAtYMin = predictX(inverseRegression, axisYMin);
      const xAtYMax = predictX(inverseRegression, axisYMax);

      series.push({
        type: 'line',
        name: 'X on Y Line',
        data: [
          [xAtYMin, axisYMin],
          [xAtYMax, axisYMax],
        ],
        symbol: 'none',
        lineStyle: {
          color: inverseRegressionColor,
          width: 2,
        },
        z: 2,
        silent: true,
      });
    }

    // Residual lines
    if (display.showResiduals && regression) {
      const residuals = computeResiduals(points, regression);

      for (const r of residuals) {
        series.push({
          type: 'line',
          name: 'Residuals',
          data: [
            [r.x, r.actual],
            [r.x, r.predicted],
          ],
          symbol: 'none',
          lineStyle: {
            color: residualColor,
            width: 1.5,
            type: 'dashed',
          },
          z: 1,
          silent: true,
        } as Record<string, unknown>);
      }
    }

    // Prediction point
    if (pred && regression) {
      series.push({
        type: 'scatter',
        name: 'Prediction',
        data: [[pred.x, pred.y]],
        symbolSize: 14,
        symbol: 'diamond',
        itemStyle: {
          color: '#f59e0b',
          borderColor: '#fff',
          borderWidth: 2,
        },
        z: 4,
      });

      series.push({
        type: 'line',
        data: [
          [pred.x, axisYMin],
          [pred.x, pred.y],
        ],
        symbol: 'none',
        lineStyle: { color: '#f59e0b', width: 1, type: 'dashed' },
        z: 1,
        silent: true,
      } as Record<string, unknown>);
      series.push({
        type: 'line',
        data: [
          [axisXMin, pred.y],
          [pred.x, pred.y],
        ],
        symbol: 'none',
        lineStyle: { color: '#f59e0b', width: 1, type: 'dashed' },
        z: 1,
        silent: true,
      } as Record<string, unknown>);
    }

    // Mean point with crosshair lines
    if (display.showMeanPoint && points.length > 0) {
      const meanX = points.reduce((s, p) => s + p.x, 0) / points.length;
      const meanY = points.reduce((s, p) => s + p.y, 0) / points.length;

      const meanLineColor = '#6b7280';
      const meanLineStyle = { color: meanLineColor, width: 1.5, type: 'dashed' as const };

      // Vertical dashed line at x̄
      series.push({
        type: 'line',
        data: [
          [meanX, axisYMin],
          [meanX, axisYMax],
        ],
        symbol: 'none',
        lineStyle: meanLineStyle,
        z: 1,
        silent: true,
      } as Record<string, unknown>);

      // x̄ label at top of vertical line
      series.push({
        type: 'scatter',
        data: [[meanX, axisYMax]],
        symbolSize: 0,
        label: {
          show: true,
          formatter: '{var|x̄}',
          position: 'top',
          rich: {
            var: { fontSize: 13, fontWeight: 600, fontStyle: 'italic', color: meanLineColor, fontFamily: 'serif' },
          },
        },
        z: 6,
        silent: true,
      } as Record<string, unknown>);

      // Horizontal dashed line at ȳ
      series.push({
        type: 'line',
        data: [
          [axisXMin, meanY],
          [axisXMax, meanY],
        ],
        symbol: 'none',
        lineStyle: meanLineStyle,
        z: 1,
        silent: true,
      } as Record<string, unknown>);

      // ȳ label at right of horizontal line
      series.push({
        type: 'scatter',
        data: [[axisXMax, meanY]],
        symbolSize: 0,
        label: {
          show: true,
          formatter: '{var|ȳ}',
          position: 'right',
          rich: {
            var: { fontSize: 13, fontWeight: 600, fontStyle: 'italic', color: meanLineColor, fontFamily: 'serif' },
          },
        },
        z: 6,
        silent: true,
      } as Record<string, unknown>);

      // Mean point diamond marker
      series.push({
        type: 'scatter',
        name: 'Mean Point',
        data: [[meanX, meanY]],
        symbolSize: 14,
        symbol: 'diamond',
        itemStyle: {
          color: '#f97316',
          borderColor: '#fff',
          borderWidth: 2,
        },
        z: 4,
      });
    }

    // ── Static regression explanation annotations ──
    if (display.explainRegressionLine && display.showRegressionLine && regression) {
      const explainRich = {
        var: { fontStyle: 'italic' as const, fontWeight: 600 as const, fontFamily: 'serif' },
        val: { fontWeight: 700 as const },
      };
      const explainLabelBox = {
        backgroundColor: 'rgba(255,255,255,0.92)',
        padding: [3, 8] as [number, number],
        borderRadius: 4,
      };
      const explainName = 'Y on X Line'; // ties to legend toggle

      // ── Intercept (b) ──
      // Y-intercept dot at (0, b)
      series.push({
        type: 'scatter',
        name: explainName,
        data: [[0, regression.b]],
        symbolSize: 12,
        symbol: 'circle',
        itemStyle: { color: regressionColor, borderColor: '#fff', borderWidth: 2 },
        label: {
          show: true,
          formatter: `{var|b} = {val|${fmt(regression.b)}}`,
          position: 'right',
          fontSize: 12,
          color: regressionColor,
          ...explainLabelBox,
          borderColor: regressionColor,
          borderWidth: 1,
          rich: explainRich,
        },
        z: 7,
        silent: true,
      } as Record<string, unknown>);

      // Dotted vertical line at x = 0
      series.push({
        type: 'line',
        name: explainName,
        data: [
          [0, axisYMin],
          [0, regression.b],
        ],
        symbol: 'none',
        lineStyle: { color: regressionColor, width: 1.5, type: 'dotted' },
        z: 5,
        silent: true,
      } as Record<string, unknown>);

      // ── Slope triangle (a) ──
      const sDeltaX = xRange * 0.3;
      const sXRef = (xMin + xMax) / 2 - sDeltaX / 2;
      const sYRef = predictY(regression, sXRef);
      const sXEnd = sXRef + sDeltaX;
      const sYEnd = predictY(regression, sXEnd);
      const sRise = regression.a * sDeltaX;

      // Horizontal leg (run)
      series.push({
        type: 'line',
        name: explainName,
        data: [
          [sXRef, sYRef],
          [sXEnd, sYRef],
        ],
        symbol: 'none',
        lineStyle: { color: regressionColor, width: 2, type: 'dashed' },
        z: 5,
        silent: true,
      } as Record<string, unknown>);

      // Vertical leg (rise)
      series.push({
        type: 'line',
        name: explainName,
        data: [
          [sXEnd, sYRef],
          [sXEnd, sYEnd],
        ],
        symbol: 'none',
        lineStyle: { color: regressionColor, width: 2, type: 'dashed' },
        z: 5,
        silent: true,
      } as Record<string, unknown>);

      // Δx label
      series.push({
        type: 'scatter',
        name: explainName,
        data: [[(sXRef + sXEnd) / 2, sYRef]],
        symbolSize: 0,
        label: {
          show: true,
          formatter: `{var|Δx} = {val|${fmt(sDeltaX)}}`,
          position: regression.a >= 0 ? 'bottom' : 'top',
          fontSize: 12,
          fontWeight: 600,
          color: regressionColor,
          ...explainLabelBox,
          rich: explainRich,
        },
        z: 6,
        silent: true,
      } as Record<string, unknown>);

      // Δy label
      series.push({
        type: 'scatter',
        name: explainName,
        data: [[sXEnd, (sYRef + sYEnd) / 2]],
        symbolSize: 0,
        label: {
          show: true,
          formatter: `{var|Δy} = {val|${fmt(sRise)}}`,
          position: 'right',
          fontSize: 12,
          fontWeight: 600,
          color: regressionColor,
          ...explainLabelBox,
          rich: explainRich,
        },
        z: 6,
        silent: true,
      } as Record<string, unknown>);

      // a = Δy/Δx label at triangle corner
      series.push({
        type: 'scatter',
        name: explainName,
        data: [[sXEnd, sYRef]],
        symbolSize: 0,
        label: {
          show: true,
          formatter: `{var|a} = {var|Δy}/{var|Δx} = {val|${fmt(regression.a)}}`,
          position: regression.a >= 0 ? 'bottomRight' : 'topRight',
          fontSize: 12,
          fontWeight: 600,
          color: regressionColor,
          ...explainLabelBox,
          rich: explainRich,
        },
        z: 6,
        silent: true,
      } as Record<string, unknown>);
    }

    // ── Slope triangle annotation ──
    if (hoveredSection === 'slope' && regression) {
      const xMid = (xMin + xMax) / 2;
      const deltaX = xRange * 0.35;
      const xRef = xMid - deltaX / 2;
      const yRef = predictY(regression, xRef);
      const xEnd = xRef + deltaX;
      const yEnd = predictY(regression, xEnd);
      const rise = regression.a * deltaX;

      // Horizontal leg (run)
      series.push({
        type: 'line',
        data: [
          [xRef, yRef],
          [xEnd, yRef],
        ],
        symbol: 'none',
        lineStyle: { color: regressionColor, width: 2.5, type: 'dashed' },
        z: 5,
        silent: true,
      } as Record<string, unknown>);

      // Vertical leg (rise)
      series.push({
        type: 'line',
        data: [
          [xEnd, yRef],
          [xEnd, yEnd],
        ],
        symbol: 'none',
        lineStyle: { color: regressionColor, width: 2.5, type: 'dashed' },
        z: 5,
        silent: true,
      } as Record<string, unknown>);

      // Δx label
      series.push({
        type: 'scatter',
        data: [[(xRef + xEnd) / 2, yRef]],
        symbolSize: 0,
        label: {
          show: true,
          formatter: `Δx = ${fmt(deltaX)}`,
          position: regression.a >= 0 ? 'bottom' : 'top',
          fontSize: 12,
          fontWeight: 600,
          color: regressionColor,
          backgroundColor: 'rgba(255,255,255,0.92)',
          padding: [3, 8],
          borderRadius: 4,
        },
        z: 6,
        silent: true,
      } as Record<string, unknown>);

      // Δy label
      series.push({
        type: 'scatter',
        data: [[xEnd, (yRef + yEnd) / 2]],
        symbolSize: 0,
        label: {
          show: true,
          formatter: `Δy = ${fmt(rise)}`,
          position: 'right',
          fontSize: 12,
          fontWeight: 600,
          color: regressionColor,
          backgroundColor: 'rgba(255,255,255,0.92)',
          padding: [3, 8],
          borderRadius: 4,
        },
        z: 6,
        silent: true,
      } as Record<string, unknown>);

      // Slope = Δy / Δx label at corner
      series.push({
        type: 'scatter',
        data: [[xEnd, yRef]],
        symbolSize: 8,
        symbol: 'rect',
        itemStyle: { color: regressionColor },
        label: {
          show: true,
          formatter: `a = Δy/Δx = ${fmt(regression.a)}`,
          position: regression.a >= 0 ? 'bottomRight' : 'topRight',
          fontSize: 12,
          fontWeight: 700,
          color: regressionColor,
          backgroundColor: 'rgba(255,255,255,0.92)',
          padding: [4, 10],
          borderRadius: 4,
        },
        z: 6,
        silent: true,
      } as Record<string, unknown>);
    }

    // ── Intercept dot annotation ──
    if (hoveredSection === 'intercept' && regression) {
      // Y-intercept dot at (0, b)
      series.push({
        type: 'scatter',
        data: [[0, regression.b]],
        symbolSize: 14,
        symbol: 'circle',
        itemStyle: {
          color: regressionColor,
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: {
          show: true,
          formatter: `b = ${fmt(regression.b)}`,
          position: 'right',
          fontSize: 12,
          fontWeight: 700,
          color: regressionColor,
          backgroundColor: 'rgba(255,255,255,0.92)',
          padding: [3, 8],
          borderRadius: 4,
          borderColor: regressionColor,
          borderWidth: 1,
        },
        z: 7,
        silent: true,
      } as Record<string, unknown>);

      // Dotted vertical line at x=0
      series.push({
        type: 'line',
        data: [
          [0, axisYMin],
          [0, regression.b],
        ],
        symbol: 'none',
        lineStyle: { color: regressionColor, width: 1.5, type: 'dotted' },
        z: 5,
        silent: true,
      } as Record<string, unknown>);
    }

    // Data zoom — filterMode 'none' keeps all series visible (regression line spans full range)
    const dataZoomStyle = {
      borderColor: '#e5e7eb',
      fillerColor: 'rgba(14,165,233,0.08)',
      handleStyle: { color: '#0ea5e9', borderColor: '#0ea5e9' },
      dataBackground: { lineStyle: { color: '#d1d5db' }, areaStyle: { color: '#f3f4f6' } },
      selectedDataBackground: { lineStyle: { color: '#0ea5e9' }, areaStyle: { color: 'rgba(14,165,233,0.12)' } },
      textStyle: { fontSize: 10, color: '#9ca3af' },
    };
    const dataZoom: Array<Record<string, unknown>> = [];
    if (zoomX) {
      dataZoom.push({
        type: 'slider',
        xAxisIndex: 0,
        filterMode: 'none',
        bottom: 28,
        height: 20,
        ...dataZoomStyle,
      });
    }
    if (zoomY) {
      dataZoom.push({
        type: 'slider',
        yAxisIndex: 0,
        filterMode: 'none',
        left: 16,
        width: 20,
        ...dataZoomStyle,
      });
    }

    // Tooltip helpers
    const tooltipRow = (label: string, value: string, opts?: { valueColor?: string }) => {
      const vc = opts?.valueColor ?? '#374151';
      return (
        `<div style="display:flex;align-items:baseline;justify-content:space-between;gap:16px;padding:1.5px 0">` +
        `<span style="color:#9ca3af;font-size:11.5px">${label}</span>` +
        `<span style="font-weight:500;font-size:12.5px;color:${vc};font-variant-numeric:tabular-nums">${value}</span>` +
        `</div>`
      );
    };
    const tooltipHeader = (label: string, color: string) =>
      `<div style="font-weight:600;font-size:13px;color:${color};padding-bottom:3px;margin-bottom:3px;border-bottom:1px solid #f3f4f6">${label}</div>`;
    const tooltipDivider = () => `<div style="border-top:1px solid #f3f4f6;margin:5px 0 4px"></div>`;

    return {
      animationDuration: isRegressionHovered ? 300 : 500,
      tooltip: {
        show: true,
        trigger: 'item',
        confine: true,
        backgroundColor: '#fff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        extraCssText: 'box-shadow:0 4px 16px rgba(0,0,0,0.08),0 1px 3px rgba(0,0,0,0.04);border-radius:8px;',
        padding: [10, 14],
        textStyle: { fontSize: 12, color: '#374151' },
        formatter: (params: unknown) => {
          const p = params as { seriesName?: string; dataIndex?: number; value?: number[] };
          if (!p.value) {
            return '';
          }

          const xVal = p.value[0];
          const yVal = p.value[1];

          if (p.seriesName === 'Prediction') {
            return [tooltipRow(xLabel, fmt(xVal)), tooltipRow(yLabel, fmt(yVal))].join('');
          }

          if (p.seriesName === 'Mean Point') {
            return [
              tooltipHeader('Mean Point', '#f97316'),
              tooltipRow(xLabel, fmt(xVal)),
              tooltipRow(yLabel, fmt(yVal)),
            ].join('');
          }

          const lines: string[] = [];

          // Show point label header (A, B, C...) when enabled
          if (display.showPointLabels && p.dataIndex !== null && p.dataIndex !== undefined) {
            const pointLabel = String.fromCharCode(65 + p.dataIndex);
            lines.push(tooltipHeader(`Student ${pointLabel}`, pointLabelColor));
          }

          lines.push(tooltipRow(xLabel, fmt(xVal)));
          lines.push(tooltipRow(yLabel, fmt(yVal)));

          // Show predicted value and residual when regression line is visible
          if (display.showRegressionLine && regression) {
            const predicted = predictY(regression, xVal);
            const residual = yVal - predicted;
            const sign = residual >= 0 ? '+' : '';

            lines.push(tooltipDivider());
            lines.push(tooltipRow('Predicted', fmt(predicted), { valueColor: regressionColor }));
            lines.push(tooltipRow('Residual', `${sign}${fmt(residual)}`, { valueColor: residualColor }));
          }

          return lines.join('');
        },
      },
      legend: {
        show: showLegend,
        top: 0,
        right: 0,
        itemWidth: 16,
        itemHeight: 10,
        itemGap: 16,
        textStyle: { fontSize: 11, color: '#6b7280' },
        data: [
          { name: 'Observation', icon: 'circle' },
          ...(display.showRegressionLine && hasActiveRegression
            ? [
                {
                  name: regressionLegendName,
                  icon: 'path://M0,4L16,4L16,6L0,6Z',
                  itemStyle: { color: regressionColor },
                },
              ]
            : []),
          ...(display.showInverseRegressionLine && inverseRegression
            ? [
                {
                  name: 'X on Y Line',
                  icon: 'path://M0,4L16,4L16,6L0,6Z',
                  itemStyle: { color: inverseRegressionColor },
                },
              ]
            : []),
          ...(display.showResiduals && regression
            ? [
                {
                  name: 'Residuals',
                  icon: 'path://M0,4L4,4L4,6L0,6Z M6,4L10,4L10,6L6,6Z M12,4L16,4L16,6L12,6Z',
                  itemStyle: { color: residualColor },
                },
              ]
            : []),
          ...(display.showMeanPoint ? [{ name: 'Mean Point', icon: 'diamond', itemStyle: { color: '#f97316' } }] : []),
        ],
      },
      dataZoom: dataZoom.length > 0 ? dataZoom : undefined,
      grid: {
        top: display.showMeanPoint ? (showLegend ? 42 : 34) : showLegend ? 28 : 20,
        bottom: zoomX ? 80 : 45,
        left: zoomY ? 92 : 55,
        right: display.showMeanPoint ? 35 : 25,
      },
      xAxis: {
        type: 'value',
        name: xLabel,
        nameLocation: 'middle',
        nameGap: zoomX ? 64 : 28,
        nameTextStyle: { fontSize: 12, color: '#6b7280' },
        min: zoomX ? undefined : axisXMin,
        max: zoomX ? undefined : axisXMax,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#d1d5db' } },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { fontSize: 12, color: '#6b7280', formatter: fmtAxisLabel },
      },
      yAxis: {
        type: 'value',
        name: yLabel,
        nameLocation: 'middle',
        nameGap: zoomY ? 80 : 42,
        nameTextStyle: { fontSize: 12, color: '#6b7280' },
        min: zoomY ? undefined : axisYMin,
        max: zoomY ? undefined : axisYMax,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#d1d5db' } },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { fontSize: 12, color: '#6b7280', formatter: fmtAxisLabel },
      },
      series,
    };
  });

  readonly adaptedOptions = computed(() => adaptEchartsOptions(this.chartOptions(), this.document, this.isDark()));

  onChartMouseOver(event: { seriesName?: string; dataIndex?: number }): void {
    // eslint-disable-next-line eqeqeq -- intentional nullish check (null | undefined)
    if (event.seriesName === 'Observation' && event.dataIndex != null) {
      this.pointHover.emit({ index: event.dataIndex, source: 'chart' });
    }
  }

  onChartMouseOut(event: { seriesName?: string }): void {
    if (event.seriesName === 'Observation') {
      this.pointHover.emit({ index: null, source: 'chart' });
    }
  }
}

/** Pick a "nice" rounding step for axis bounds so tick labels stay clean. */
function niceStep(range: number): number {
  const magnitude = Math.pow(10, Math.floor(Math.log10(range || 1)));
  const fraction = range / magnitude;
  if (fraction <= 1.5) {
    return magnitude / 5;
  }
  if (fraction <= 3) {
    return magnitude / 2;
  }
  if (fraction <= 7) {
    return magnitude;
  }
  return magnitude * 2;
}

function buildEmptyChart(xLabel: string, yLabel: string): EChartsOption {
  return {
    grid: { top: 20, bottom: 45, left: 55, right: 25 },
    xAxis: {
      type: 'value',
      name: xLabel,
      nameLocation: 'middle',
      nameGap: 28,
      nameTextStyle: { fontSize: 12, color: '#6b7280' },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#d1d5db' } },
      splitLine: { lineStyle: { color: '#f3f4f6' } },
      axisLabel: { fontSize: 12, color: '#6b7280' },
    },
    yAxis: {
      type: 'value',
      name: yLabel,
      nameLocation: 'middle',
      nameGap: 42,
      nameTextStyle: { fontSize: 12, color: '#6b7280' },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#d1d5db' } },
      splitLine: { lineStyle: { color: '#f3f4f6' } },
      axisLabel: { fontSize: 12, color: '#6b7280' },
    },
    series: [],
  };
}
